const CPF_REGEX = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/

function calculateDigit(digits: number[]) {
  const sum = digits.reduce(
    (previousValue, currentValue, index) => previousValue + currentValue * (digits.length + 1 - index),
    0
  )
  const quotient = (sum * 10) % 11
  return quotient === 10 ? 0 : quotient
}

/**
 * Validates a CPF (Brazilian individual taxpayer registry identification).
 *
 * @param cpf - The CPF string to validate. It can be either masked (e.g., "123.456.789-09")
 *                       or unmasked (e.g., "12345678909").
 * @param withMaskValidation - Whether to validate the CPF format with a mask. (Default: `false`)
 *                                               If true, the input must match the masked format.
 * @returns `true` if the CPF is valid, `false` otherwise.
 *
 * @example
 * isValidCpf("123.456.789-09"); // true
 * isValidCpf("12345678909"); // true
 * isValidCpf("12345678909", true); // false (invalid without mask)
 */
export function isValidCpf(cpf: string, withMaskValidation = false) {
  const unmaskedCpf = cpf.replace(/\D/g, '')

  if (withMaskValidation && !CPF_REGEX.test(cpf)) {
    return false
  }

  if (unmaskedCpf.length !== 11) {
    return false
  }

  if (/^(\d)\1{10}/g.test(unmaskedCpf)) {
    return false
  }

  const lastTwoDigits = unmaskedCpf.slice(-2)

  const firstNineDigits = unmaskedCpf.split('').slice(0, 9).map(Number)
  const tenDigit = calculateDigit(firstNineDigits)

  if (Number(lastTwoDigits[0]) !== tenDigit) {
    return false
  }

  const firstTenDigits = [...firstNineDigits, tenDigit]
  const elevenDigit = calculateDigit(firstTenDigits)

  if (Number(lastTwoDigits[1]) !== elevenDigit) {
    return false
  }

  return true
}
